import { NOTE_COLORS, normalizeNoteColor } from '../constants/note-colors';
import api from './api';
import { db, getMeta, setMeta } from './idb';

function nowIso() {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16 | 0;
    const value = char === 'x' ? rand : (rand & 0x3 | 0x8);
    return value.toString(16);
  });
}

function normalizeTask(task) {
  if (!task) return task;
  return {
    ...task,
    sort_order: Number(task.sort_order || 0),
    completed: Number(task.completed || 0),
    completed_by_user_id: task.completed_by_user_id == null ? null : Number(task.completed_by_user_id),
    image_width: task.image_width == null ? null : Number(task.image_width),
    image_height: task.image_height == null ? null : Number(task.image_height),
    image_size_bytes: task.image_size_bytes == null ? null : Number(task.image_size_bytes),
  };
}

export function isTaskCompleted(task) {
  return Number(task?.completed || 0) === 1;
}

function currentDeviceId() {
  let value = localStorage.getItem('notas_device_id');
  if (!value) {
    value = uuidv4();
    localStorage.setItem('notas_device_id', value);
  }
  return value;
}

export async function queueOperation(action, entityType, entityId, payload, scopeType, scopeId) {
  const op = {
    op_id: uuidv4(),
    device_id: currentDeviceId(),
    entity_type: entityType,
    entity_id: entityId,
    action,
    payload,
    scope_type: scopeType,
    scope_id: scopeId || 'personal',
    client_ts: nowIso(),
    status: 'pending',
    created_at: nowIso(),
  };

  await db.operations.put(op);
  return op;
}

export async function applyListLocal(list) {
  await db.lists.put(list);
}

export async function applyTaskLocal(task) {
  await db.tasks.put(normalizeTask(task));
}

export async function removeTaskLocal(taskId, deletedAt = nowIso()) {
  const task = await db.tasks.get(taskId);
  if (!task) return;
  await db.tasks.put({ ...task, deleted_at: deletedAt, updated_at: deletedAt });
}

export async function removeListLocal(listId, deletedAt = nowIso()) {
  const list = await db.lists.get(listId);
  if (!list) return;
  await db.lists.put({ ...list, deleted_at: deletedAt, updated_at: deletedAt });
  const tasks = await db.tasks.where('list_id').equals(listId).toArray();
  await Promise.all(tasks.map((task) => db.tasks.put({ ...task, deleted_at: deletedAt, updated_at: deletedAt })));
}

export async function loadLocalState() {
  const lists = (await db.lists.toArray()).filter((item) => !item.deleted_at);
  const tasks = (await db.tasks.toArray()).filter((item) => !item.deleted_at).map((item) => normalizeTask(item));
  return { lists, tasks };
}

export async function ensureGuestInbox() {
  const inbox = await db.lists.get('guest-inbox');
  if (!inbox) {
    await db.lists.put({
      id: 'guest-inbox',
      owner_user_id: null,
      author_user_id: null,
      author_name: null,
      group_id: null,
      title: 'Hoja rápida',
      color: NOTE_COLORS[0],
      sort_order: 0,
      body_updated_at: nowIso(),
      updated_at: nowIso(),
      created_at: nowIso(),
      archived_at: null,
      deleted_at: null,
      last_activity_at: nowIso(),
      guest_only: true,
    });
  }
}

export async function bootstrapRemote() {
  const response = await api.get('/bootstrap');
  const data = response.data?.data || {};

  if (data.mode === 'user') {
    const remoteLists = data.lists || [];
    const remoteTasks = (data.tasks || []).map((item) => normalizeTask(item));
    const remoteListIds = new Set(remoteLists.map((item) => item.id));
    const remoteTaskIds = new Set(remoteTasks.map((item) => item.id));

    await db.transaction('rw', db.lists, db.tasks, async () => {
      const localLists = await db.lists.toArray();
      const localTasks = await db.tasks.toArray();

      await Promise.all(localLists
        .filter((item) => item.id !== 'guest-inbox' && !remoteListIds.has(item.id))
        .map((item) => db.lists.delete(item.id)));

      await Promise.all(localTasks
        .filter((item) => item.list_id !== 'guest-inbox' && !remoteTaskIds.has(item.id))
        .map((item) => db.tasks.delete(item.id)));

      for (const list of remoteLists) {
        const current = await db.lists.get(list.id);
        await db.lists.put({
          ...current,
          ...list,
          color: normalizeNoteColor(list.color || current?.color),
          author_user_id: list.author_user_id == null ? current?.author_user_id ?? null : Number(list.author_user_id),
          author_name: list.author_name ?? current?.author_name ?? null,
          owner_user_id: list.owner_user_id == null ? current?.owner_user_id ?? null : Number(list.owner_user_id),
          is_quick_sheet: Number(list.is_quick_sheet ?? current?.is_quick_sheet ?? 0),
        });
      }

      for (const task of remoteTasks) {
        await db.tasks.put(task);
      }
    });
  }

  return data;
}

export async function flushPending() {
  const token = localStorage.getItem('notas_access_token');
  if (!token || !navigator.onLine) return { skipped: true };

  const operations = await db.operations.where('status').equals('pending').sortBy('created_at');
  if (!operations.length) return { skipped: true };

  const response = await api.post('/sync/push', {
    device_id: currentDeviceId(),
    operations: operations.map((op) => ({
      op_id: op.op_id,
      entity_type: op.entity_type,
      entity_id: op.entity_id,
      action: op.action,
      payload: op.payload,
      scope_type: op.scope_type,
      scope_id: op.scope_id,
      client_ts: op.client_ts,
    })),
  });

  const results = response.data?.data?.results || [];
  for (const result of results) {
    const existing = await db.operations.get(result.op_id);
    if (!existing) continue;
    await db.operations.put({
      ...existing,
      status: result.outcome === 'applied' ? 'done' : result.outcome,
      seq: result.seq,
      conflict_code: result.conflict_code || null,
    });
  }

  const cursor = response.data?.data?.cursor;
  if (cursor) {
    await setMeta('sync_cursor', cursor);
  }

  return response.data?.data || {};
}

export async function pullRemote() {
  const token = localStorage.getItem('notas_access_token');
  if (!token || !navigator.onLine) return { skipped: true };

  const cursor = await getMeta('sync_cursor', 0);
  const response = await api.get('/sync/pull', { params: { cursor } });
  const data = response.data?.data || {};
  const operations = data.operations || [];

  for (const op of operations) {
    await applyIncomingOperation(op);
  }

  if (data.cursor !== undefined) {
    await setMeta('sync_cursor', data.cursor);
  }

  return data;
}

export async function applyIncomingOperation(op) {
  const payload = op.payload || {};
  switch (op.action) {
    case 'create_list':
      await db.lists.put({
        id: op.entity_id,
        owner_user_id: op.scope_type === 'personal' ? Number(op.actor_user_id || 0) : null,
        author_user_id: Number(op.actor_user_id || payload.author_user_id || 0) || null,
        author_name: payload.author_name || null,
        group_id: op.scope_type === 'group' ? op.scope_id : null,
        is_quick_sheet: op.scope_type === 'personal' ? Number(payload.is_quick_sheet || 0) : 0,
        title: payload.title || 'Nueva hoja',
        color: normalizeNoteColor(payload.color),
        sort_order: Number(payload.sort_order || 0),
        body_updated_at: op.client_ts,
        updated_at: op.client_ts,
        created_at: op.client_ts,
        archived_at: null,
        deleted_at: null,
        last_activity_at: op.client_ts,
      });
      break;
    case 'update_list': {
      const current = await db.lists.get(op.entity_id);
      if (!current) break;
      await db.lists.put({
        ...current,
        title: payload.title ?? current.title,
        color: normalizeNoteColor(payload.color || current.color),
        sort_order: Number(payload.sort_order ?? current.sort_order),
        body_updated_at: op.client_ts,
        updated_at: op.client_ts,
        last_activity_at: op.client_ts,
      });
      break;
    }
    case 'move_list': {
      const current = await db.lists.get(op.entity_id);
      if (!current) break;
      const targetScopeType = payload.target_scope_type || (current.group_id ? 'group' : 'personal');
      await db.lists.put({
        ...current,
        owner_user_id: targetScopeType === 'personal' ? Number(payload.owner_user_id || op.actor_user_id || current.owner_user_id || 0) : null,
        group_id: targetScopeType === 'group' ? (payload.target_scope_id || payload.group_id || current.group_id || null) : null,
        is_quick_sheet: targetScopeType === 'personal'
          ? Number(payload.is_quick_sheet ?? current.is_quick_sheet ?? 0)
          : 0,
        title: payload.title ?? current.title,
        color: normalizeNoteColor(payload.color || current.color),
        sort_order: Number(payload.sort_order ?? current.sort_order),
        body_updated_at: op.client_ts,
        updated_at: op.client_ts,
        last_activity_at: op.client_ts,
      });
      break;
    }
    case 'delete_list':
      await removeListLocal(op.entity_id, op.client_ts);
      break;
    case 'create_task':
      await db.tasks.put(normalizeTask({
        id: op.entity_id,
        list_id: payload.list_id,
        body: payload.body || 'Nueva tarea',
        sort_order: Number(payload.sort_order || 0),
        completed: 0,
        completed_at: null,
        completed_by_user_id: null,
        body_updated_at: op.client_ts,
        completion_updated_at: op.client_ts,
        updated_at: op.client_ts,
        created_at: op.client_ts,
        deleted_at: null,
      }));
      break;
    case 'update_task_text': {
      const currentTask = await db.tasks.get(op.entity_id);
      if (!currentTask) break;
      await db.tasks.put(normalizeTask({
        ...currentTask,
        body: payload.body ?? currentTask.body,
        sort_order: Number(payload.sort_order ?? currentTask.sort_order),
        body_updated_at: op.client_ts,
        updated_at: op.client_ts,
      }));
      break;
    }
    case 'set_task_completed': {
      const currentTask = await db.tasks.get(op.entity_id);
      if (!currentTask) break;
      const completed = !!payload.completed;
      await db.tasks.put(normalizeTask({
        ...currentTask,
        completed: completed ? 1 : 0,
        completed_at: completed ? op.client_ts : null,
        completion_updated_at: op.client_ts,
        updated_at: op.client_ts,
      }));
      break;
    }
    case 'set_task_image': {
      const currentTask = await db.tasks.get(op.entity_id);
      if (!currentTask) break;
      await db.tasks.put(normalizeTask({
        ...currentTask,
        image_path: payload.image_path ?? null,
        image_mime: payload.image_mime ?? null,
        image_width: payload.image_width ?? null,
        image_height: payload.image_height ?? null,
        image_size_bytes: payload.image_size_bytes ?? null,
        image_updated_at: payload.image_updated_at ?? op.client_ts,
        image_url: payload.image_path ? `${(process.env.VUE_APP_API_URL || 'https://notas.test/api').replace(/\/api\/?$/, '')}/${String(payload.image_path).replace(/^\//, '')}` : null,
        updated_at: op.client_ts,
      }));
      break;
    }
    case 'remove_task_image': {
      const currentTask = await db.tasks.get(op.entity_id);
      if (!currentTask) break;
      await db.tasks.put(normalizeTask({
        ...currentTask,
        image_path: null,
        image_mime: null,
        image_width: null,
        image_height: null,
        image_size_bytes: null,
        image_updated_at: payload.image_updated_at ?? op.client_ts,
        image_url: null,
        updated_at: op.client_ts,
      }));
      break;
    }
    case 'delete_task':
      await removeTaskLocal(op.entity_id, op.client_ts);
      break;
    default:
      break;
  }
}

export function getDeviceMeta() {
  return {
    device_id: currentDeviceId(),
    device_name: navigator.userAgent,
    device_platform: navigator.platform || 'web',
  };
}

export async function clearGuestPendingOperations() {
  const guestTaskIds = new Set((await db.tasks.where('list_id').equals('guest-inbox').toArray()).map((item) => item.id));
  const operations = await db.operations.toArray();
  const staleGuestOps = operations.filter((op) => {
    if ((op.scope_type || 'personal') !== 'personal') return false;
    if (op.entity_type === 'list' && op.entity_id === 'guest-inbox') return true;
    if (op.entity_type === 'task' && guestTaskIds.has(op.entity_id)) return true;
    if ((op.payload?.list_id || '') === 'guest-inbox') return true;
    return false;
  });

  if (!staleGuestOps.length) {
    return 0;
  }

  await Promise.all(staleGuestOps.map((op) => db.operations.delete(op.op_id)));
  return staleGuestOps.length;
}

export async function resetLocalForLogout() {
  await setMeta('sync_cursor', 0);
  const allLists = await db.lists.toArray();
  const accountLists = allLists.filter((item) => item.id !== 'guest-inbox');
  await Promise.all(accountLists.map((item) => db.lists.delete(item.id)));

  await db.operations.clear();
  const allTasks = await db.tasks.toArray();
  const guestTaskIds = new Set(allTasks.filter((item) => item.list_id === 'guest-inbox').map((item) => item.id));
  await Promise.all(allTasks.filter((item) => !guestTaskIds.has(item.id)).map((item) => db.tasks.delete(item.id)));
}
